/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
import React, { useCallback, useMemo, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { Box, Inline, Pressable, Stack, Text, xcss } from '@atlaskit/primitives';
import Lozenge from '@atlaskit/lozenge';
import { token } from '@atlaskit/tokens';
import CalendarIcon from '@atlaskit/icon/core/migration/calendar';
import PageIcon from '@atlaskit/icon/core/migration/page';
import ArchiveIcon from '@atlaskit/icon/core/migration/archive-box--archive';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import Image from '@atlaskit/image';
import { StaggeredEntrance, FadeIn } from '@atlaskit/motion';

import CustomCursorLight from '../images/designer-cursor-light.svg';
import CustomCursorDark from '../images/designer-cursor-dark.svg';

const i18n = defineMessages({
	archiveInactivePagesExampleTitle1: {
		id: 'premium-trial.bite-sized.modal.automation.example.archive-inactive-pages.title-1',
		defaultMessage: 'Reduce clutter by auto-archiving inactive pages',
		description: 'Title for archive inactive pages example',
	},
	archiveInactivePagesExampleTitle2: {
		id: 'premium-trial.bite-sized.modal.automation.example.archive-inactive-pages.title-2',
		defaultMessage: 'I want to automatically archive inactive pages',
		description: 'Title for archive inactive pages example',
	},
	archiveInactivePagesExampleRecurrenceSettings: {
		id: 'premium-trial.bite-sized.modal.automation.example.archive-inactive-pages.recurrence-settings',
		defaultMessage: 'Every {weekDay} at {time}',
		description: 'Recurrence settings for archive inactive pages example',
	},
	archiveInactivePagesExampleInActiveLengthSettings: {
		id: 'premium-trial.bite-sized.modal.automation.example.archive-inactive-pages.inactive-length-settings',
		defaultMessage: 'If pages have no activity within the last {monthSecltor} ',
		description: 'Inactive length for archive inactive pages example',
	},
	archiveInactivePagesExampleAction: {
		id: 'premium-trial.bite-sized.modal.automation.example.archive-inactive-pages.action',
		defaultMessage: 'Then archive the page',
		description: 'Action for archive inactive pages example',
	},
	automationExampleLabel: {
		id: 'premium-trial.bite-sized.modal.automation.example.label',
		defaultMessage: 'Automation example',
		description: 'Label for automation example',
	},
	automationIndicatorText: {
		id: 'premium-trial.bite-sized.modal.automation.example.indicator-text',
		defaultMessage: 'Select to edit the text',
		description: 'Indicator text for automation rule step',
	},
	monthsLabel: {
		id: 'premium-trial.bite-sized.modal.automation.example.months.label',
		defaultMessage: '{month, plural, one {# month} other {# months}}',
		description: 'Months label for automation months duration dropdown',
	},
});

const stepsBoxStyles = xcss({
	position: 'relative',
});

const stepStyles = xcss({
	width: '357px',
	borderRadius: '20px',
	background: token('elevation.surface.raised'),
	border: `1px solid ${token('color.background.accent.gray.subtler')}`,
});

const iconBoxStyles = xcss({
	borderRadius: 'border.radius',
});

const dropdownTriggerStyles = xcss({
	color: 'color.text',
	display: 'inline-block',
	cursor: 'pointer',
});

const underlineStyles = xcss({
	textDecorationLine: 'underline',
});

const noUnderlineStyles = xcss({
	textDecorationLine: 'none',
});

const lineStyles = xcss({
	background: token('color.background.accent.gray.subtlest.hovered'),
	width: '2px',
	height: '32px',
	marginLeft: 'space.400',
});

const indicatorStyles = xcss({
	position: 'absolute',
	top: '48px',
	left: '135px',
});

const indicatorTextBoxStyles = xcss({
	borderRadius: '32px',
	background: token('color.background.accent.purple.subtle'),
	marginTop: 'space.150',
});

type ArchiveAutomationRuleSettingsExampleProps = {
	isAutomationPanel?: boolean;
};

const AutomationRuleStep = ({
	icon,
	step,
	isFirst,
}: {
	icon: ReactNode;
	step: ReactNode;
	isFirst: boolean;
}) => {
	return (
		<Stack>
			{!isFirst && <Box xcss={lineStyles} />}
			<Box padding="space.200" xcss={stepStyles}>
				<Inline space="space.150" grow="fill" alignBlock="center">
					{icon}
					{step}
				</Inline>
			</Box>
		</Stack>
	);
};

const IconBox = ({ icon, backgroundColor }: { icon: ReactNode; backgroundColor: any }) => {
	return (
		<Box padding="space.075" xcss={iconBoxStyles} backgroundColor={backgroundColor}>
			{icon}
		</Box>
	);
};

const WeekDaySelectionDropdown = ({
	onUserInteraction,
	lastStepRendered,
}: {
	onUserInteraction: () => void;
	lastStepRendered: boolean;
}) => {
	const { locale } = useIntl();
	const formatter = useMemo(() => new Intl.DateTimeFormat(locale, { weekday: 'long' }), [locale]);

	const [weekDay, setWeekDay] = useState(formatter.format(new Date(2024, 0, 5)));

	const weekDays = useMemo(() => {
		return Array.from({ length: 5 }, (_, i) => {
			const date = new Date(2024, 0, i + 1); // January 1, 2024 was Monday
			return formatter.format(date);
		});
	}, [formatter]);
	return (
		<DropdownMenu
			trigger={({ triggerRef, ...providedProps }) => (
				<Pressable
					padding="space.0"
					backgroundColor="color.background.neutral.subtle"
					{...providedProps}
					ref={triggerRef}
					xcss={[dropdownTriggerStyles, lastStepRendered ? underlineStyles : noUnderlineStyles]}
					onClick={(e) => {
						providedProps?.onClick?.(e);
						onUserInteraction();
					}}
				>
					{weekDay}
				</Pressable>
			)}
			placement="bottom-start"
		>
			<DropdownItemGroup>
				{weekDays.map((day, index) => (
					<DropdownItem key={index} onClick={() => setWeekDay(day)}>
						{day}
					</DropdownItem>
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};

const TimeSelectionDropdown = ({
	onUserInteraction,
	lastStepRendered,
}: {
	onUserInteraction: () => void;
	lastStepRendered: boolean;
}) => {
	const { formatTime } = useIntl();

	// Localize the initial time value
	const initialTime = useMemo(
		() =>
			formatTime(new Date('2000-01-01T09:00:00'), {
				hour: 'numeric',
				minute: 'numeric',
				hour12: false,
			}),
		[formatTime],
	);

	const [time, setTime] = useState(initialTime);
	// Function to generate localized time options

	const timeOptions = useMemo(() => {
		const options = [];
		for (let i = 18; i <= 34; i++) {
			// Only iterate over times from 09:00 to 17:00
			const hours = String(Math.floor(i / 2)).padStart(2, '0');
			const minutes = i % 2 === 0 ? '00' : '30';
			const value = `${hours}:${minutes}`;
			const label = formatTime(new Date(`2000-01-01T${value}:00`), {
				hour: 'numeric',
				minute: 'numeric',
				hour12: false,
			});
			options.push({ value, label });
		}
		return options;
	}, [formatTime]);

	return (
		<DropdownMenu<HTMLButtonElement>
			trigger={({ triggerRef, ...providedProps }) => (
				<Pressable
					padding="space.0"
					backgroundColor="color.background.neutral.subtle"
					{...providedProps}
					ref={triggerRef}
					xcss={[dropdownTriggerStyles, lastStepRendered ? underlineStyles : noUnderlineStyles]}
					onClick={(e) => {
						providedProps?.onClick?.(e);
						onUserInteraction();
					}}
				>
					{time}
				</Pressable>
			)}
			placement="bottom-start"
		>
			<DropdownItemGroup>
				{timeOptions.map((timeObj, index) => (
					<DropdownItem key={index} onClick={() => setTime(timeObj.label)}>
						{timeObj.label}
					</DropdownItem>
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};

const MonthDurationSelectionDropdown = ({
	onUserInteraction,
	lastStepRendered,
}: {
	onUserInteraction: () => void;
	lastStepRendered: boolean;
}) => {
	const intl = useIntl();
	const [month, setMonth] = useState(intl.formatMessage(i18n.monthsLabel, { month: 6 }));

	const timePeriods = useMemo(() => {
		return [3, 6, 9, 12].map((months) => intl.formatMessage(i18n.monthsLabel, { month: months }));
	}, [intl]);
	return (
		<DropdownMenu<HTMLButtonElement>
			trigger={({ triggerRef, isSelected, testId, ...providedProps }) => (
				<Pressable
					padding="space.0"
					backgroundColor="color.background.neutral.subtle"
					{...providedProps}
					ref={triggerRef}
					xcss={[dropdownTriggerStyles, lastStepRendered ? underlineStyles : noUnderlineStyles]}
					onClick={(e) => {
						providedProps?.onClick?.(e);
						onUserInteraction();
					}}
				>
					{month}
				</Pressable>
			)}
		>
			<DropdownItemGroup>
				{timePeriods.map((monthItem, index) => (
					<DropdownItem key={index} onClick={() => setMonth(monthItem)}>
						{monthItem}
					</DropdownItem>
				))}
			</DropdownItemGroup>
		</DropdownMenu>
	);
};

const Indicator = () => {
	return (
		<Pressable
			xcss={indicatorStyles}
			padding="space.0"
			backgroundColor="color.background.neutral.subtle"
		>
			<Inline>
				<Image
					src={CustomCursorLight}
					srcDark={CustomCursorDark}
					alt="indicator cursor"
					testId="image"
				/>
				<Box xcss={indicatorTextBoxStyles} padding="space.150">
					<Text size="large" weight="bold">
						<FormattedMessage {...i18n.automationIndicatorText} />
					</Text>
				</Box>
			</Inline>
		</Pressable>
	);
};

export const ArchiveAutomationRuleSettingsExample: FC<
	ArchiveAutomationRuleSettingsExampleProps
> = ({ isAutomationPanel = false }) => {
	const [lastStepRendered, setLastStepRendered] = useState(false);

	const [showIndicator, setShowIndicator] = useState(false);

	const onUserInteraction = useCallback(() => {
		setShowIndicator(false);
	}, []);

	const steps = [
		{
			icon: (
				<IconBox
					icon={
						<CalendarIcon
							spacing="spacious"
							label="Calendar"
							color={token('color.icon.accent.green')}
						/>
					}
					backgroundColor="color.background.accent.green.subtlest"
				/>
			),
			step: (
				<FormattedMessage
					{...i18n.archiveInactivePagesExampleRecurrenceSettings}
					values={{
						weekDay: (
							<WeekDaySelectionDropdown
								onUserInteraction={onUserInteraction}
								lastStepRendered={lastStepRendered}
							/>
						),
						time: (
							<TimeSelectionDropdown
								onUserInteraction={onUserInteraction}
								lastStepRendered={lastStepRendered}
							/>
						),
					}}
				/>
			),
		},
		{
			icon: (
				<IconBox
					icon={
						<PageIcon spacing="spacious" label="Page" color={token('color.icon.accent.purple')} />
					}
					backgroundColor="color.background.accent.purple.subtlest"
				/>
			),
			step: (
				<FormattedMessage
					{...i18n.archiveInactivePagesExampleInActiveLengthSettings}
					values={{
						monthSecltor: (
							<MonthDurationSelectionDropdown
								onUserInteraction={onUserInteraction}
								lastStepRendered={lastStepRendered}
							/>
						),
					}}
				/>
			),
		},
		{
			icon: (
				<IconBox
					icon={
						<ArchiveIcon
							spacing="spacious"
							label="Archive"
							color={token('color.icon.accent.blue')}
						/>
					}
					backgroundColor="color.background.accent.blue.subtlest"
				/>
			),
			step: <FormattedMessage {...i18n.archiveInactivePagesExampleAction} />,
		},
	];

	const onStepRendered = useCallback(
		(stepNumber: number) => {
			if (stepNumber === steps.length - 1) {
				setLastStepRendered(true);
				setShowIndicator(true);
				setTimeout(() => {
					setShowIndicator(false);
				}, 3000);
			}
		},
		[steps.length],
	);

	return (
		<Box>
			<Stack space="space.200">
				<Stack space="space.100">
					<Box>
						<Lozenge appearance="default">
							<FormattedMessage {...i18n.automationExampleLabel} />
						</Lozenge>
					</Box>
					<Text weight="bold" color="color.text.subtle">
						“
						{isAutomationPanel ? (
							<FormattedMessage {...i18n.archiveInactivePagesExampleTitle2} />
						) : (
							<FormattedMessage {...i18n.archiveInactivePagesExampleTitle1} />
						)}
						“
					</Text>
				</Stack>

				<Stack xcss={stepsBoxStyles}>
					{showIndicator && <Indicator />}
					<StaggeredEntrance delayStep={1000}>
						{steps.map((step, index) => (
							<FadeIn
								key={index}
								entranceDirection="top"
								onFinish={() => {
									onStepRendered(index);
								}}
							>
								{(props) => (
									<div {...props}>
										{' '}
										<AutomationRuleStep
											key={index}
											icon={step.icon}
											step={step.step}
											isFirst={index == 0}
										/>{' '}
									</div>
								)}
							</FadeIn>
						))}
					</StaggeredEntrance>
				</Stack>
			</Stack>
		</Box>
	);
};
